import { OpsHealthCard } from '../components/OpsAndSecurity';
import { TenantBillingCard } from '../components/TenantBilling';
import { PlatformAnalytics, SupportAccessCard } from '../components/SupportAndAnalytics';
import { zodResolver } from '@hookform/resolvers/zod';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building, Plus } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router';
import { z } from 'zod';
import { Badge, type BadgeTone } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardBody, CardHeader } from '../../../components/ui/Card';
import { ConfirmDialog, Dialog } from '../../../components/ui/Dialog';
import { EmptyState } from '../../../components/ui/EmptyState';
import { applyServerErrors, SelectField, TextField } from '../../../components/ui/Field';
import {
  DataTable,
  ErrorState,
  PageContainer,
  PageHeader,
  Pagination,
  TableSkeleton,
} from '../../../components/ui/Page';
import { useToast } from '../../../components/ui/Toast';
import { ApiError } from '../../../services/http';
import {
  CATEGORY_LABELS,
  FEATURE_LABELS,
  platformApi,
  platformKeys,
  type Plan,
  type PlatformTenant,
  type TenantDetail,
} from '../api';

// Charts load ApexCharts, so they come in their own chunk (docs/design/15 §5).
const PlatformCharts = lazy(() =>
  import('../components/PlatformCharts').then((m) => ({ default: m.PlatformCharts })),
);

const STATUS: Record<PlatformTenant['status'], { label: string; tone: BadgeTone }> = {
  pending_setup: { label: 'Setting up', tone: 'info' },
  active: { label: 'Active', tone: 'success' },
  suspended: { label: 'Suspended', tone: 'warning' },
  cancelled: { label: 'Cancelled', tone: 'neutral' },
};

export function PlatformOverviewPage() {
  const overview = useQuery({ queryKey: platformKeys.overview, queryFn: platformApi.overview });
  const counts = overview.data?.tenantsByStatus ?? {};
  return (
    <PageContainer>
      <PageHeader
        title="Platform overview"
        description="Aggregate numbers only. Tenant lead data is not visible here."
      />
      {overview.isError ? <ErrorState error={overview.error} /> : null}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {(Object.keys(STATUS) as PlatformTenant['status'][]).map((s) => (
          <Card key={s} className="p-5">
            <p className="text-2xl font-semibold tabular-nums">
              {overview.isPending ? '…' : (counts[s] ?? 0)}
            </p>
            <p className="text-sm text-fg-muted">{STATUS[s].label} tenants</p>
          </Card>
        ))}
      </div>
      <div className="mt-6">
        <div className="space-y-6">
          <OpsHealthCard />
          <PlatformAnalytics />
          <Suspense fallback={null}>
            <PlatformCharts />
          </Suspense>
        </div>
      </div>
    </PageContainer>
  );
}

const createSchema = z.object({
  name: z.string().trim().min(1, 'Enter the business name').max(150),
  slug: z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9](?:[a-z0-9-]{1,61}[a-z0-9])$/, '3–63 lowercase letters, digits or hyphens'),
  businessCategory: z.string(),
  planCode: z.string().min(1),
  ownerName: z.string().trim().min(1, "Enter the owner's name"),
  ownerEmail: z.string().trim().email('Enter a valid email'),
});
type CreateValues = z.infer<typeof createSchema>;

function CreateTenantDialog({ open, onOpenChange }: { open: boolean; onOpenChange(o: boolean): void }) {
  const toast = useToast();
  const navigate = useNavigate();
  const plans = useQuery({ queryKey: platformKeys.plans, queryFn: platformApi.plans, enabled: open });
  const form = useForm<CreateValues>({ resolver: zodResolver(createSchema) });
  useEffect(() => {
    if (open)
      form.reset({
        name: '',
        slug: '',
        businessCategory: 'general',
        planCode: 'starter',
        ownerName: '',
        ownerEmail: '',
      });
  }, [open, form]);
  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      platformApi.createTenant({
        name: v.name,
        slug: v.slug,
        businessCategory: v.businessCategory,
        planCode: v.planCode,
        owner: { fullName: v.ownerName, email: v.ownerEmail },
      }),
    onSuccess: (t) => {
      toast.success(
        t.ownerInviteEmailSent
          ? `Tenant created. Invitation sent to ${t.owner?.email}.`
          : 'Tenant created, but the invitation email failed. Resend it from the tenant page.',
      );
      onOpenChange(false);
      navigate(`/platform/tenants/${t.id}`);
    },
    onError: (err) =>
      err instanceof ApiError ? applyServerErrors(err.fieldErrors, form.setError as never) : undefined,
  });
  const e = form.formState.errors;
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New tenant"
      description="Creates the business account and emails its owner an invitation."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" form="tenant-form" loading={create.isPending}>
            Create tenant
          </Button>
        </>
      }
    >
      <form
        id="tenant-form"
        noValidate
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={form.handleSubmit((v) => create.mutate(v))}
      >
        {create.error && !(create.error instanceof ApiError && create.error.code === 'VALIDATION_FAILED') ? (
          <div className="sm:col-span-2">
            <ErrorState error={create.error} />
          </div>
        ) : null}
        <TextField label="Business name" error={e.name?.message} {...form.register('name')} />
        <TextField
          label="Workspace"
          hint="Used to sign in, e.g. acme"
          error={e.slug?.message}
          {...form.register('slug')}
        />
        <SelectField
          label="Business type"
          options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label }))}
          {...form.register('businessCategory')}
        />
        <SelectField
          label="Plan"
          options={(plans.data ?? [])
            .filter((p) => p.status === 'active')
            .map((p) => ({ value: p.code, label: p.name }))}
          {...form.register('planCode')}
        />
        <TextField label="Owner name" error={e.ownerName?.message} {...form.register('ownerName')} />
        <TextField
          label="Owner email"
          type="email"
          error={e.ownerEmail?.message}
          {...form.register('ownerEmail')}
        />
      </form>
    </Dialog>
  );
}

export function TenantsPage() {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');
  const query = { page, ...(q ? { q } : {}) };
  const tenants = useQuery({
    queryKey: platformKeys.tenants(query),
    queryFn: () => platformApi.tenants(query),
    placeholderData: keepPreviousData,
  });
  return (
    <PageContainer>
      <PageHeader
        title="Tenants"
        actions={
          <Button leadingIcon={<Plus aria-hidden className="size-4" />} onClick={() => setCreating(true)}>
            New tenant
          </Button>
        }
      />
      <Card>
        <div className="border-b border-line p-4">
          <input
            type="search"
            aria-label="Search tenants"
            placeholder="Search by name or workspace"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full max-w-sm rounded-md border border-line bg-surface px-3 text-sm"
          />
        </div>
        {tenants.isPending ? (
          <TableSkeleton />
        ) : tenants.isError ? (
          <div className="p-4">
            <ErrorState error={tenants.error} />
          </div>
        ) : (
          <>
            <DataTable
              rows={tenants.data.data}
              rowKey={(t) => t.id}
              onRowClick={(t) => navigate(`/platform/tenants/${t.id}`)}
              empty={<EmptyState icon={<Building className="size-6" />} title="No tenants yet" />}
              columns={[
                {
                  header: 'Business',
                  cell: (t) => (
                    <div>
                      <p className="font-medium text-fg">{t.name}</p>
                      <p className="text-xs text-fg-subtle">{t.slug}</p>
                    </div>
                  ),
                },
                { header: 'Plan', cell: (t) => t.plan?.name ?? '—' },
                { header: 'Staff', cell: (t) => <span className="tabular-nums">{t.staffCount ?? 0}</span> },
                {
                  header: 'Status',
                  cell: (t) => <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge>,
                },
                { header: 'Created', cell: (t) => new Date(t.createdAt).toLocaleDateString() },
              ]}
            />
            <Pagination page={page} pageSize={25} total={tenants.data.meta.total ?? 0} onPage={setPage} />
          </>
        )}
      </Card>
      <CreateTenantDialog open={creating} onOpenChange={setCreating} />
    </PageContainer>
  );
}

function FeaturesCard({ tenant }: { tenant: TenantDetail }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const set = useMutation({
    mutationFn: (features: Parameters<typeof platformApi.setFeatures>[1]) =>
      platformApi.setFeatures(tenant.id, features),
    onSuccess: (data) => {
      queryClient.setQueryData(platformKeys.tenant(tenant.id), data);
      toast.success('Entitlements updated');
    },
    onError: (err) => toast.error(err),
  });
  const overridden = new Set(tenant.featureOverrides.map((o) => o.key));
  return (
    <Card>
      <CardHeader title="Entitlements" description="Plan defaults, with per-tenant overrides highlighted." />
      <ul className="divide-y divide-line">
        {Object.entries(tenant.entitlements).map(([key, state]) => (
          <li key={key} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
            <span className="flex-1 text-fg">{FEATURE_LABELS[key] ?? key}</span>
            {overridden.has(key) ? <Badge tone="warning">Override</Badge> : null}
            {key.startsWith('limit.') ? (
              <input
                aria-label={`${FEATURE_LABELS[key]} limit`}
                type="number"
                min={0}
                defaultValue={state.limit ?? ''}
                placeholder="Unlimited"
                onBlur={(e) => {
                  const raw = e.target.value.trim();
                  const limit = raw === '' ? null : Number(raw);
                  if (limit !== state.limit)
                    set.mutate({ [key]: { enabled: true, limit, reason: 'Platform console' } });
                }}
                className="h-8 w-28 rounded-md border border-line bg-surface px-2 text-right tabular-nums"
              />
            ) : (
              <input
                type="checkbox"
                role="switch"
                aria-label={FEATURE_LABELS[key]}
                className="size-4 accent-brand-600"
                checked={state.enabled}
                onChange={(e) =>
                  set.mutate({ [key]: { enabled: e.target.checked, reason: 'Platform console' } })
                }
              />
            )}
            {overridden.has(key) ? (
              <Button size="sm" variant="ghost" onClick={() => set.mutate({ [key]: null })}>
                Reset
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

export function TenantDetailPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const tenant = useQuery({ queryKey: platformKeys.tenant(id), queryFn: () => platformApi.tenant(id) });
  const plans = useQuery({ queryKey: platformKeys.plans, queryFn: platformApi.plans });
  const [reason, setReason] = useState('');

  async function run(fn: () => Promise<TenantDetail | { emailSent: boolean }>, message: string) {
    try {
      const result = await fn();
      if ('slug' in result) queryClient.setQueryData(platformKeys.tenant(id), result);
      await queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      toast.success(message);
    } catch (err) {
      toast.error(err);
    }
  }

  if (tenant.isPending)
    return (
      <PageContainer>
        <TableSkeleton />
      </PageContainer>
    );
  if (tenant.isError)
    return (
      <PageContainer>
        <ErrorState error={tenant.error} />
      </PageContainer>
    );
  const t = tenant.data;

  return (
    <PageContainer>
      <PageHeader
        back={{ to: '/platform/tenants', label: 'Tenants' }}
        title={t.name}
        description={
          <span className="flex items-center gap-2">
            <code>{t.slug}</code> <Badge tone={STATUS[t.status].tone}>{STATUS[t.status].label}</Badge>
          </span>
        }
        actions={
          <>
            {t.status === 'suspended' ? (
              <Button
                variant="secondary"
                onClick={() => void run(() => platformApi.setStatus(t.id, 'activate'), 'Tenant activated')}
              >
                Activate
              </Button>
            ) : null}
            {t.status === 'active' || t.status === 'pending_setup' ? (
              <ConfirmDialog
                title={`Suspend ${t.name}?`}
                description="Everyone in this tenant is signed out and cannot sign in until it is activated again."
                confirmLabel={`Suspend ${t.name}`}
                onConfirm={() =>
                  run(() => platformApi.setStatus(t.id, 'suspend', reason), 'Tenant suspended')
                }
                trigger={(open) => (
                  <Button variant="danger" onClick={open}>
                    Suspend
                  </Button>
                )}
              >
                <TextField
                  label="Reason"
                  required
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  hint="Recorded in the audit log"
                />
              </ConfirmDialog>
            ) : null}
          </>
        }
      />
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title="Account" />
          <CardBody>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-fg-subtle">Owner</dt>
                <dd className="text-fg">{t.owner ? `${t.owner.fullName} (${t.owner.status})` : '—'}</dd>
                <dd className="text-xs text-fg-subtle">{t.owner?.email}</dd>
              </div>
              <div>
                <dt className="text-fg-subtle">Business type</dt>
                <dd className="text-fg">{CATEGORY_LABELS[t.businessCategory] ?? t.businessCategory}</dd>
              </div>
              <div>
                <dt className="text-fg-subtle">Staff / branches</dt>
                <dd className="tabular-nums text-fg">
                  {t.counts.staff} / {t.counts.branches}
                </dd>
              </div>
              <div>
                <dt className="text-fg-subtle">Setup</dt>
                <dd className="text-fg">
                  {t.onboardingStep === 'done' ? 'Complete' : `At step: ${t.onboardingStep}`}
                </dd>
              </div>
            </dl>
            {t.owner?.status === 'invited' ? (
              <Button
                className="mt-4"
                size="sm"
                variant="secondary"
                onClick={() => void run(() => platformApi.resendOwnerInvite(t.id), 'Invitation sent again')}
              >
                Resend owner invitation
              </Button>
            ) : null}
            {t.suspendedReason ? (
              <p className="mt-4 text-sm text-warning">Suspended: {t.suspendedReason}</p>
            ) : null}
          </CardBody>
        </Card>
        <Card>
          <CardHeader
            title="Subscription"
            description={
              t.subscription ? `Since ${new Date(t.subscription.startsAt).toLocaleDateString()}` : undefined
            }
          />
          <CardBody>
            <SelectField
              label="Plan"
              value={t.subscription?.plan?.code ?? ''}
              onChange={(e) =>
                void run(() => platformApi.setSubscription(t.id, e.target.value), 'Plan changed')
              }
              options={(plans.data ?? [])
                .filter((p) => p.status === 'active')
                .map((p) => ({ value: p.code, label: p.name }))}
            />
          </CardBody>
        </Card>
        <div className="lg:col-span-2">
          <TenantBillingCard tenant={t} />
        </div>
        <div className="lg:col-span-2">
          <FeaturesCard tenant={t} />
        </div>
        <div className="lg:col-span-2">
          <SupportAccessCard tenantId={t.id} />
        </div>
      </div>
    </PageContainer>
  );
}

function PlanPriceDialog({ plan, onOpenChange }: { plan: Plan | null; onOpenChange(open: boolean): void }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  // Mounted with key={plan.id}, so the fields start from the plan being edited.
  const [price, setPrice] = useState(plan?.priceAmount ?? '');
  const [currency, setCurrency] = useState(plan?.currency ?? 'INR');
  const [interval, setInterval] = useState(plan?.billingInterval ?? 'month');
  const [trialDays, setTrialDays] = useState(String(plan?.trialDays ?? 0));
  const save = useMutation({
    mutationFn: () =>
      platformApi.updatePlan(plan!.id, {
        priceAmount: price.trim() === '' ? null : price.trim(),
        currency,
        billingInterval: interval,
        trialDays: Number(trialDays) || 0,
      }),
    onSuccess: async () => {
      toast.success('Plan updated');
      onOpenChange(false);
      await queryClient.invalidateQueries({ queryKey: platformKeys.plans });
    },
    onError: (err) => toast.error(err),
  });
  return (
    <Dialog
      open={plan !== null}
      onOpenChange={onOpenChange}
      title={plan ? `Price and trial: ${plan.name}` : ''}
      description="Leave the price empty to show 'Contact us' on the sign-up and billing pages."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={() => save.mutate()}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField
          label="Price"
          inputMode="decimal"
          placeholder="Not published"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
        />
        <TextField
          label="Currency"
          maxLength={3}
          value={currency}
          onChange={(e) => setCurrency(e.target.value.toUpperCase())}
        />
        <SelectField
          label="Billed every"
          value={interval}
          onChange={(e) => setInterval(e.target.value)}
          options={[
            { value: 'month', label: 'Month' },
            { value: 'year', label: 'Year' },
            { value: 'custom', label: 'Custom' },
          ]}
        />
        <TextField
          label="Trial days (platform-created tenants)"
          type="number"
          min={0}
          max={365}
          value={trialDays}
          onChange={(e) => setTrialDays(e.target.value)}
        />
      </div>
    </Dialog>
  );
}

export function PlansPage() {
  const plans = useQuery({ queryKey: platformKeys.plans, queryFn: platformApi.plans });
  const [editing, setEditing] = useState<Plan | null>(null);
  return (
    <PageContainer>
      <PageHeader
        title="Plans"
        description="Limits and modules each plan includes. Tenants can get overrides on their own page."
      />
      {plans.isPending ? (
        <TableSkeleton />
      ) : plans.isError ? (
        <ErrorState error={plans.error} />
      ) : (
        <div className="grid gap-4 md:grid-cols-3">
          {plans.data.map((p) => (
            <Card key={p.id}>
              <CardHeader
                title={p.name}
                description={p.description}
                actions={<Badge tone={p.status === 'active' ? 'success' : 'neutral'}>{p.status}</Badge>}
              />
              <div className="flex items-center justify-between px-5 pb-3 text-sm">
                <span className="tabular-nums text-fg">
                  {p.priceAmount
                    ? `${p.currency} ${p.priceAmount} / ${p.billingInterval}`
                    : 'No price published'}
                </span>
                <Button size="sm" variant="ghost" onClick={() => setEditing(p)}>
                  Edit price
                </Button>
              </div>
              <ul className="divide-y divide-line text-sm">
                {Object.entries(FEATURE_LABELS).map(([key, label]) => {
                  const f = p.features[key];
                  return (
                    <li key={key} className="flex justify-between px-5 py-2">
                      <span className="text-fg-muted">{label}</span>
                      <span className="tabular-nums text-fg">
                        {key.startsWith('limit.')
                          ? f
                            ? f.limit === null
                              ? 'Unlimited'
                              : f.limit
                            : '—'
                          : f?.enabled
                            ? 'Included'
                            : '—'}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </Card>
          ))}
        </div>
      )}
      <PlanPriceDialog
        key={editing?.id ?? 'none'}
        plan={editing}
        onOpenChange={(o) => !o && setEditing(null)}
      />
    </PageContainer>
  );
}
